import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import GymCreateForm from './GymCreateForm';
import { uploadGymLogoFile } from './GymLogoUploadField';

export default function GymCreatePage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { getAuthenticatedApi, isAdmin } = useAuth();
    const [error, setError] = useState(null);

    if (!isAdmin()) {
        return <Navigate to="/admin/gyms" replace />;
    }

    const handleSubmit = async (gymData, logoFile) => {
        try {
            setError(null);
            const api = getAuthenticatedApi();
            const res = await api.post('/gyms.php', gymData);
            const gymId = res.data?.gym?.id;
            if (gymId && logoFile) {
                try {
                    await uploadGymLogoFile(api, gymId, logoFile);
                } catch (logoErr) {
                    console.error('Upload logo dopo creazione:', logoErr);
                }
            }
            navigate('/admin/gyms', {
                state: {
                    gymCreated: true,
                    createdOwner: res.data?.owner
                        ? {
                            username: res.data.owner.username,
                            name: res.data.owner.name,
                            password: gymData.owner_password
                        }
                        : null
                }
            });
        } catch (err) {
            console.error('Errore nella creazione palestra:', err);
            setError(err.response?.data?.error || t('admin.gymManager.createError'));
            throw err;
        }
    };

    return (
        <div className="p-4 sm:p-6 bg-gray-50 dark:bg-gray-900 min-h-screen w-full">
            <div className="w-full max-w-[1600px] mx-auto">
                <div className="mb-4 sm:mb-5">
                    <Link
                        to="/admin/gyms"
                        className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white mb-2 transition-colors"
                    >
                        <ArrowLeft size={16} />
                        {t('admin.gymManager.backToList')}
                    </Link>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                        <Building className="text-blue-600 shrink-0" />
                        {t('admin.gymManager.createTitle')}
                    </h1>
                    <p className="text-gray-600 dark:text-gray-300 mt-1 text-sm sm:text-base max-w-4xl">
                        {t('admin.gymManager.createDescription')}
                    </p>
                </div>

                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                        {error}
                    </div>
                )}

                <GymCreateForm
                    onSubmit={handleSubmit}
                    onCancel={() => navigate('/admin/gyms')}
                />
            </div>
        </div>
    );
}
